import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';

import css from './SectionPackageContents.module.css';

/**
 * SectionPackageContents — items included in the package.
 *
 * Refactored to match listing-detail-C.html's "What's included":
 * an h2 heading + small muted subtitle ("Seats up to N guests")
 * followed by a 2-column responsive grid of items. Each row is a
 * sage ✓ icon + item name on the left and a quantity on the right,
 * separated from the next row by a thin divider.
 *
 * No card wrapper — the mockup keeps the section flush with the
 * surrounding flow (see SectionAvailableAddOns / SectionLogistics
 * for the same convention).
 *
 * When `selectedTierKey` is provided and the listing has tiers,
 * only the selected tier's items are shown — EXCEPT when the tier's
 * item list leads with an "All items from Basic Tier"-style
 * pseudo-item (the wizard convention for delta-authored upper
 * tiers). Then the base tier's REAL items render first under an
 * "Everything in {Basic}" header, followed by "Plus, in {Deluxe}"
 * with the delta items, so upgrading never hides what the base
 * includes (UI/UX audit item 11). No pseudo-item → render the
 * tier's list as-is (some hosts author full standalone lists).
 */

// "All items from Basic Tier" / "All items in the Basic tier" /
// "Everything in Basic" … — the free-text pseudo-item hosts write as
// the first row of a delta-authored tier. Matched loosely but
// anchored: must start with all/everything + from/in + the base
// tier's name (or the word tier).
const isBaseTierPseudoItem = (name, baseTierName) => {
  if (typeof name !== 'string') return false;
  const base = (baseTierName || 'basic').toLowerCase();
  const n = name.trim().toLowerCase();
  if (!/^(all( the)?( items?)?|everything)\s+(from|in|of)\b/.test(n)) return false;
  return n.includes(base) || /\btier\b/.test(n);
};

const ItemRow = ({ item, idx }) => {
  let displayQty = null;
  if (item.quantity === 'per-guest') {
    // Quantity scales with the renter's actual party, so show the
    // per-guest meaning — never a fixed number. maxGuestCount is
    // the CAP, not the party size, so "×N" was both misleading and
    // indistinguishable from a fixed quantity of N.
    displayQty = 'one per guest';
  } else if (typeof item.quantity === 'number' && item.quantity >= 1) {
    displayQty = `×${item.quantity}`;
  } else if (typeof item.quantity === 'string' && item.quantity !== 'per-guest') {
    displayQty = item.quantity;
  }
  return (
    <div key={`${item.name}-${idx}`} className={css.row}>
      <span className={css.check} aria-hidden="true">
        ✓
      </span>
      <span className={css.name}>{item.name}</span>
      {displayQty && <span className={css.qty}>{displayQty}</span>}
    </div>
  );
};

const SectionPackageContents = ({
  listing,
  selectedTierKey,
  guestCount,
  className = '',
}) => {
  const publicData = listing?.attributes?.publicData || {};
  const tiers = publicData.tiers;
  const packageItems = publicData.packageItems;

  let items = [];
  // Delta view: base tier's items + the selected tier's additions.
  let baseItems = null;
  let baseTierName = null;
  let selectedTierName = null;

  if (tiers && Object.keys(tiers).length > 0) {
    const tierKeys = Object.keys(tiers);
    const tierKey = selectedTierKey || tierKeys[0];
    const tier = tiers[tierKey];
    items = tier?.items || [];

    const baseKey = tierKeys[0];
    if (tierKey !== baseKey && items.length > 0) {
      const baseTier = tiers[baseKey];
      if (isBaseTierPseudoItem(items[0]?.name, baseTier?.name)) {
        baseItems = baseTier?.items || [];
        baseTierName = baseTier?.name || 'Basic';
        selectedTierName = tier?.name || 'Deluxe';
        items = items.slice(1);
      }
    }
  } else if (packageItems && packageItems.length > 0) {
    items = packageItems;
  }

  if (items.length === 0 && !(baseItems && baseItems.length > 0)) return null;

  const seats = guestCount || publicData.maxGuestCount;
  const subtitle = seats ? `Seats up to ${seats} guests` : null;

  return (
    <section className={classNames(css.root, className)}>
      <h2 className={css.title}>
        <FormattedMessage
          id="SectionPackageContents.title"
          defaultMessage="What's included"
        />
      </h2>
      {subtitle && <p className={css.subtitle}>{subtitle}</p>}
      {baseItems ? (
        <>
          <h3 className={css.tierGroupHeading}>
            <FormattedMessage
              id="SectionPackageContents.everythingInBase"
              defaultMessage="Everything in {tierName}"
              values={{ tierName: baseTierName }}
            />
          </h3>
          <div className={css.grid}>
            {baseItems.map((item, idx) => (
              <ItemRow key={`base-${item.name}-${idx}`} item={item} idx={idx} />
            ))}
          </div>
          {items.length > 0 && (
            <>
              <h3 className={classNames(css.tierGroupHeading, css.plusHeading)}>
                <FormattedMessage
                  id="SectionPackageContents.plusInTier"
                  defaultMessage="Plus, in {tierName}"
                  values={{ tierName: selectedTierName }}
                />
              </h3>
              <div className={css.grid}>
                {items.map((item, idx) => (
                  <ItemRow key={`delta-${item.name}-${idx}`} item={item} idx={idx} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className={css.grid}>
          {items.map((item, idx) => (
            <ItemRow key={`${item.name}-${idx}`} item={item} idx={idx} />
          ))}
        </div>
      )}
    </section>
  );
};

export default SectionPackageContents;
